import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Check, Sparkles, Crown, ArrowLeft } from "lucide-react";

const CURRENCIES = [
  { code: "eur", symbol: "€" },
  { code: "usd", symbol: "$" },
  { code: "gbp", symbol: "£" },
];

const PREMIUM_FEATURES_KEYS = ["feat1", "feat2", "feat3", "feat4"];

export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [interval, setInterval] = useState("month");
  const [currency, setCurrency] = useState("eur");
  const [loading, setLoading] = useState(false);

  const sym = CURRENCIES.find((c) => c.code === currency)?.symbol || "€";
  const price = interval === "month" ? "7.99" : "79.90";

  const checkout = async () => {
    setLoading(true);
    try {
      const lookup_key = `premium_${interval}ly_${currency}`;
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (user?.premium) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto text-center py-16 animate-fade-up" data-testid="pricing-already-premium">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl brand-bg mb-4">
            <Crown className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-display">{t("billing.alreadyPremiumTitle")}</h1>
          <p className="text-muted-foreground mt-2">{t("billing.alreadyPremiumDesc")}</p>
          <Button className="mt-6 brand-bg text-white" onClick={() => navigate("/dashboard")} data-testid="pricing-go-dashboard">
            {t("billing.backToDashboard")}
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground mb-4" data-testid="pricing-back">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>

      <div className="max-w-md mx-auto animate-fade-up">
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold brand-text bg-accent/60 rounded-full px-3 py-1">
            <Sparkles className="h-3.5 w-3.5" /> {t("billing.badge")}
          </div>
          <h1 className="text-3xl font-bold font-display tracking-tight mt-3">{t("billing.title")}</h1>
          <p className="text-muted-foreground mt-2 text-sm">{t("billing.subtitle")}</p>
        </div>

        {/* interval toggle */}
        <div className="mt-6 flex items-center justify-center gap-1 bg-secondary rounded-full p-1 w-fit mx-auto" data-testid="pricing-interval-toggle">
          {["month", "year"].map((iv) => (
            <button
              key={iv}
              data-testid={`pricing-interval-${iv}`}
              onClick={() => setInterval(iv)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${interval === iv ? "bg-card shadow-sm brand-text" : "text-muted-foreground"}`}
            >
              {t(`billing.${iv}ly`)}
              {iv === "year" && <span className="ml-1 text-[10px] brand-text">{t("billing.save")}</span>}
            </button>
          ))}
        </div>

        <div className="mt-6 bg-card border-2 border-[color:var(--brand)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 brand-text" />
              <span className="font-semibold font-display text-lg">ANIKI Premium</span>
            </div>
            {/* currency selector */}
            <div className="flex items-center gap-1 bg-secondary rounded-lg p-0.5" data-testid="pricing-currency">
              {CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  data-testid={`pricing-currency-${c.code}`}
                  onClick={() => setCurrency(c.code)}
                  className={`px-2 py-0.5 rounded text-xs font-semibold ${currency === c.code ? "bg-card brand-text shadow-sm" : "text-muted-foreground"}`}
                >
                  {c.symbol}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-end gap-1">
            <span className="text-4xl font-bold font-display" data-testid="pricing-amount">{sym}{price}</span>
            <span className="text-muted-foreground mb-1 text-sm">/ {t(`billing.${interval}`)}</span>
          </div>

          <ul className="mt-5 space-y-2.5">
            {PREMIUM_FEATURES_KEYS.map((k) => (
              <li key={k} className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 brand-text mt-0.5 shrink-0" />
                <span>{t(`billing.${k}`)}</span>
              </li>
            ))}
          </ul>

          <Button
            data-testid="pricing-checkout-button"
            onClick={checkout}
            disabled={loading}
            className="w-full mt-6 brand-bg text-white hover:opacity-90 h-11"
          >
            {loading ? t("common.loading") : t("billing.subscribeCta")}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center mt-3">{t("billing.secureNote")}</p>
        </div>
      </div>
    </Layout>
  );
}
